import React from 'react';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid2';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import { Edit, Save, Cancel, Delete } from '@mui/icons-material';
import { abyssColors } from '@/theme/colors';
import { humanizeString } from '@/utils/string';

export interface ColumnDefinition<T> {
  key: keyof T;
  label: string;
  width: number;
  type: 'text' | 'number' | 'select';
  options?: Array<{ value: string; label: string }>;
  placeholder?: string;
  inputProps?: React.InputHTMLAttributes<HTMLInputElement>;
  defaultValue?: string | number;
}

interface ConfigurationTableProps<T extends { id: string }> {
  items: T[];
  columns: ColumnDefinition<T>[];
  editingId: string | null;
  emptyMessage: string;
  onEditStart: (item: T) => void;
  onEditCancel: () => void;
  onEditSave: (id: string, field: keyof Omit<T, 'id'>, value: string | number) => void;
  onInlineChange: (id: string, field: keyof Omit<T, 'id'>, value: string | number) => void;
  onAddNew: (newItem: Omit<T, 'id'>) => void;
  onDelete?: (item: T) => void;
}

export function ConfigurationTable<T extends { id: string }>({
  items,
  columns,
  editingId,
  emptyMessage,
  onEditStart,
  onEditCancel,
  onEditSave,
  onInlineChange,
  onAddNew,
  onDelete,
}: ConfigurationTableProps<T>) {
  const [isAddingNew, setIsAddingNew] = React.useState(false);
  const [newRowData, setNewRowData] = React.useState<Record<string, string | number>>({});
  const [deleteConfirmOpen, setDeleteConfirmOpen] = React.useState(false);
  const [itemToDelete, setItemToDelete] = React.useState<T | null>(null);

  const initializeNewRow = () => {
    const initialData: Record<string, string | number> = {};
    columns.forEach((column) => {
      if (column.defaultValue !== undefined) {
        initialData[String(column.key)] = column.defaultValue;
      } else {
        initialData[String(column.key)] = '';
      }
    });
    return initialData;
  };

  const handleAddNewRow = () => {
    setIsAddingNew(true);
    setNewRowData(initializeNewRow());
  };

  const handleCancelAdd = () => {
    setIsAddingNew(false);
    setNewRowData({});
  };

  const handleSaveNew = () => {
    const newItem = { ...newRowData } as Omit<T, 'id'>;
    onAddNew(newItem);
    setIsAddingNew(false);
    setNewRowData({});
  };

  const handleNewRowChange = (field: string, value: string | number) => {
    setNewRowData((prev) => ({ ...prev, [field]: value }));
  };

  const handleDeleteClick = (item: T) => {
    setItemToDelete(item);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (itemToDelete && onDelete) {
      onDelete(itemToDelete);
    }
    setDeleteConfirmOpen(false);
    setItemToDelete(null);
  };

  const handleDeleteCancel = () => {
    setDeleteConfirmOpen(false);
    setItemToDelete(null);
  };

  const renderCell = (item: T, column: ColumnDefinition<T>, isEditing: boolean) => {
    const value = item[column.key];

    if (!isEditing) {
      return <Typography fontSize="1.2rem">{humanizeString(String(value))}</Typography>;
    }

    const selectedValues = items
      .filter((otherItem) => otherItem.id !== item.id)
      .map((otherItem) => otherItem[column.key]);

    const availableOptions =
      column.options?.filter((option) => !selectedValues.includes(option.value as T[keyof T])) ||
      [];

    switch (column.type) {
      case 'select':
        return (
          <TextField
            select
            size="small"
            value={value}
            onChange={(e) =>
              onInlineChange(item.id, column.key as keyof Omit<T, 'id'>, e.target.value)
            }
            fullWidth
          >
            {availableOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        );
      case 'number':
        return (
          <TextField
            type="number"
            size="small"
            value={value}
            onChange={(e) =>
              onInlineChange(item.id, column.key as keyof Omit<T, 'id'>, e.target.value)
            }
            fullWidth
            inputProps={column.inputProps}
          />
        );
      case 'text':
      default:
        return (
          <TextField
            size="small"
            value={value}
            onChange={(e) =>
              onInlineChange(item.id, column.key as keyof Omit<T, 'id'>, e.target.value)
            }
            fullWidth
            placeholder={column.placeholder}
          />
        );
    }
  };

  const renderNewRowCell = (column: ColumnDefinition<T>) => {
    const value = newRowData[String(column.key)] || '';
    const selectedValues = items
      .filter((otherItem) => otherItem.id !== '')
      .map((otherItem) => otherItem[column.key]);

    const availableOptions =
      column.options?.filter((option) => !selectedValues.includes(option.value as T[keyof T])) ||
      [];

    switch (column.type) {
      case 'select':
        return (
          <TextField
            select
            size="small"
            value={value}
            onChange={(e) => handleNewRowChange(String(column.key), e.target.value)}
            fullWidth
          >
            {availableOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        );
      case 'number':
        return (
          <TextField
            type="number"
            size="small"
            value={value}
            onChange={(e) => handleNewRowChange(String(column.key), e.target.value)}
            fullWidth
            inputProps={column.inputProps}
          />
        );
      case 'text':
      default:
        return (
          <TextField
            size="small"
            value={value}
            onChange={(e) => handleNewRowChange(String(column.key), e.target.value)}
            fullWidth
            placeholder={column.placeholder}
          />
        );
    }
  };

  const isNewRowValid = () => {
    return columns.every((column) => {
      const value = newRowData[String(column.key)];

      if (column.type === 'number') {
        return value !== '' && value !== undefined && !isNaN(Number(value)) && Number(value) >= 0;
      }

      if (column.type === 'select') {
        return (
          value !== '' &&
          value !== undefined &&
          column.options?.some((option) => option.value === value)
        );
      }

      return value !== '' && value !== undefined && value.toString().trim() !== '';
    });
  };

  return (
    <Grid size={{ xs: 12 }}>
      <Grid
        container
        direction="column"
        sx={{
          border: `0.1rem solid ${abyssColors.primary[100]}`,
          borderRadius: '0.4rem',
        }}
      >
        {/* Table Header */}
        <Grid
          container
          sx={{
            height: '4rem',
            borderBottom: `0.1rem solid ${abyssColors.primary[100]}`,
            backgroundColor: abyssColors.primary[50],
          }}
        >
          {columns.map((column, index) => (
            <Grid
              key={String(column.key)}
              size={{ xs: column.width }}
              sx={{
                px: '0.8rem',
                py: '0.8rem',
                borderRight:
                  index < columns.length - 1 ? `0.1rem solid ${abyssColors.primary[100]}` : 'none',
                display: 'flex',
                alignItems: 'center',
                justifyContent: column.key === 'actions' ? 'center' : 'flex-start',
              }}
            >
              <Typography variant="body1" fontWeight={600} fontSize="1.2rem">
                {column.label}
              </Typography>
            </Grid>
          ))}
          <Grid
            size={{ xs: 2 }}
            sx={{
              px: '0.8rem',
              py: '0.8rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Typography variant="body1" fontWeight={600} fontSize="1.2rem">
              Actions
            </Typography>
          </Grid>
        </Grid>

        {/* Table Body */}
        {items.length === 0 && !isAddingNew ? (
          <Grid
            container
            sx={{
              height: '8rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Typography variant="body1" color="text.secondary">
              {emptyMessage}
            </Typography>
          </Grid>
        ) : (
          <>
            {items.map((item) => {
              const isEditing = editingId === item.id;

              return (
                <>
                  <Grid
                    container
                    key={item.id}
                    sx={{
                      height: isEditing ? '5.5rem' : '4rem',
                      borderBottom: `0.1rem solid ${abyssColors.primary[100]}`,
                    }}
                  >
                    {columns.map((column, colIndex) => (
                      <Grid
                        key={String(column.key)}
                        size={{ xs: column.width }}
                        sx={{
                          px: '0.8rem',
                          py: '0.8rem',
                          borderRight:
                            colIndex < columns.length - 1
                              ? `0.1rem solid ${abyssColors.primary[100]}`
                              : 'none',
                          display: 'flex',
                          alignItems: 'center',
                          minHeight: 0,
                          overflow: 'hidden',
                        }}
                      >
                        {renderCell(item, column, isEditing)}
                      </Grid>
                    ))}
                    {/* Actions Column */}
                    <Grid
                      size={{ xs: 2 }}
                      sx={{
                        px: '0.8rem',
                        py: '0.8rem',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      {isEditing ? (
                        <Stack direction="row" spacing={1}>
                          <IconButton
                            size="small"
                            onClick={() => {
                              columns.forEach((column) => {
                                onEditSave(
                                  item.id,
                                  column.key as keyof Omit<T, 'id'>,
                                  item[column.key] as string | number
                                );
                              });
                            }}
                            sx={{ color: 'success.main' }}
                          >
                            <Save fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={onEditCancel}
                            sx={{ color: 'error.main' }}
                          >
                            <Cancel fontSize="small" />
                          </IconButton>
                        </Stack>
                      ) : (
                        <Stack direction="row" spacing={1}>
                          <IconButton
                            size="small"
                            onClick={() => onEditStart(item)}
                            sx={{ color: 'primary.main' }}
                          >
                            <Edit fontSize="small" />
                          </IconButton>
                          {onDelete && (
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteClick(item)}
                              sx={{ color: 'error.main' }}
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          )}
                        </Stack>
                      )}
                    </Grid>
                  </Grid>
                </>
              );
            })}

            {/* New Row for Adding */}
            {isAddingNew && (
              <Grid
                container
                sx={{
                  height: '5.5rem',
                  borderBottom: `0.1rem solid ${abyssColors.primary[100]}`,
                  backgroundColor: abyssColors.primary[50],
                }}
              >
                {columns.map((column, colIndex) => (
                  <Grid
                    key={String(column.key)}
                    size={{ xs: column.width }}
                    sx={{
                      px: '0.8rem',
                      py: '0.8rem',
                      borderRight:
                        colIndex < columns.length - 1
                          ? `0.1rem solid ${abyssColors.primary[100]}`
                          : 'none',
                      display: 'flex',
                      alignItems: 'center',
                      minHeight: 0,
                      overflow: 'hidden',
                    }}
                  >
                    {renderNewRowCell(column)}
                  </Grid>
                ))}
                {/* Actions Column for New Row */}
                <Grid
                  size={{ xs: 2 }}
                  sx={{
                    px: '0.8rem',
                    py: '0.8rem',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Stack direction="row" spacing={1}>
                    <IconButton
                      size="small"
                      onClick={handleSaveNew}
                      disabled={!isNewRowValid()}
                      sx={{ color: 'success.main' }}
                    >
                      <Save fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={handleCancelAdd} sx={{ color: 'error.main' }}>
                      <Cancel fontSize="small" />
                    </IconButton>
                  </Stack>
                </Grid>
              </Grid>
            )}
          </>
        )}

        {/* Add New Row Button */}
        {!isAddingNew && (
          <Grid
            container
            sx={{
              height: '3rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: abyssColors.primary[50],
              cursor: 'pointer',
              '&:hover': {
                backgroundColor: abyssColors.primary[100],
              },
            }}
            onClick={handleAddNewRow}
          >
            <Typography
              variant="body2"
              sx={{
                color: 'primary.main',
                fontWeight: 500,
              }}
            >
              + Add New Row
            </Typography>
          </Grid>
        )}
      </Grid>

      {/* Delete Confirmation Modal */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={handleDeleteCancel}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h6" fontWeight={600}>
            Confirm Delete
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            Are you sure you want to delete this configuration item? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleDeleteCancel}
            variant="outlined"
            sx={{ mr: 1 }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            variant="contained"
            color="error"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
}
